import { oc } from "@orpc/contract";
import { z } from "zod";

// --- Response schema ---

export const TaskNamespaceResponseSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  name: z.string(),
  key: z.string(),
  lastTaskNumber: z.number().int(),
  isDefault: z.boolean(),
  description: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

// Short uppercase prefix (e.g. "TASK", "ENG"). Alphanumeric, 1-10 chars.
const KeySchema = z
  .string()
  .trim()
  .min(1)
  .max(10)
  .regex(/^[A-Za-z0-9]+$/, "Key must be alphanumeric");

// --- Input schemas ---

export const CreateTaskNamespaceInputSchema = z.object({
  name: z.string().min(1).max(255),
  key: KeySchema,
  description: z.string().max(10000).nullable().optional(),
});

export const UpdateTaskNamespaceInputSchema = z.object({
  id: z.uuid(),
  name: z.string().min(1).max(255).optional(),
  key: KeySchema.optional(),
  description: z.string().max(10000).nullable().optional(),
});

// --- Contracts ---

export const taskNamespacesContracts = {
  list: oc
    .input(z.object({}))
    .output(z.array(TaskNamespaceResponseSchema))
    .route({ method: "GET" }),

  create: oc
    .input(CreateTaskNamespaceInputSchema)
    .output(TaskNamespaceResponseSchema)
    .route({ method: "POST" }),

  update: oc
    .input(UpdateTaskNamespaceInputSchema)
    .output(TaskNamespaceResponseSchema)
    .route({ method: "PUT" }),

  archive: oc
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ success: z.boolean() }))
    .route({ method: "DELETE" }),

  setDefault: oc
    .input(z.object({ id: z.uuid() }))
    .output(TaskNamespaceResponseSchema)
    .route({ method: "PUT" }),
} as const;

// --- Type exports ---

export type TaskNamespaceResponse = z.infer<typeof TaskNamespaceResponseSchema>;
export type CreateTaskNamespaceInput = z.infer<
  typeof CreateTaskNamespaceInputSchema
>;
export type UpdateTaskNamespaceInput = z.infer<
  typeof UpdateTaskNamespaceInputSchema
>;
